import { useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { SERVICE_ADMIN_ROLES } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { SectionCard } from "@/components/common/SectionCard";
import { dismissServiceResourceNote } from "@/lib/service/client";
import { ServiceBoardResourceNotesSummary } from "./ServiceBoardResourceNotesSummary";
import { ServiceCollapsibleSection } from "./ServiceCollapsibleSection";
import { ServiceDispatchBoard } from "./ServiceDispatchBoard";
import { ServiceRouteComposer } from "./ServiceRouteComposer";
import { ServiceGateBanner } from "./ServiceGateBanner";
import {
  EVENT_STATUS_CHIP,
  EVENT_STATUS_LABELS,
  ghostBtnClass,
  inputClass,
} from "./serviceShared";
import { ServiceBoardEventDetail } from "./ServiceBoardEventDetail";
import { useServiceAnchorLayerGate } from "./useServiceAnchorLayerGate";
import { useServiceBoardPanel } from "./useServiceBoardPanel";
import { useServiceResourceNotesSummary } from "./useServiceResourceNotesSummary";
import { ServiceLaborRatesSection } from "./ServiceLaborRatesPanel";
import { ServiceRequestManualEntryPanel } from "./ServiceRequestManualEntryPanel";
import { groupServiceBoardEvents } from "@/lib/service/serviceBoardQueues";
import type { ServiceEvent } from "@/lib/service/types";

function ServiceBoardEventListItem(props: {
  ev: ServiceEvent;
  selected: boolean;
  onToggle: () => void;
}) {
  const { ev } = props;
  return (
    <li
      className={`flex flex-wrap items-center justify-between gap-2 py-2 ${
        props.selected ? "bg-indigo-50/80 dark:bg-indigo-950/30" : ""
      }`}
    >
      <button type="button" className="text-left" onClick={props.onToggle}>
        <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
          {ev.title || ev.location_label || "Untitled event"}
        </div>
        <div className="text-xs text-gray-500 dark:text-zinc-400">
          {ev.job_kind === "internal" ? "Internal" : "Sold"}
          {ev.customer_contacted_at ? " · contacted" : " · not contacted"}
          {" · "}
          {ev.scheduled_date ?? "Unscheduled"}
          {ev.scheduled_window_label ? ` · ${ev.scheduled_window_label}` : ""}
        </div>
      </button>
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${EVENT_STATUS_CHIP[ev.status] ?? EVENT_STATUS_CHIP.unscheduled}`}
      >
        {EVENT_STATUS_LABELS[ev.status] ?? ev.status}
      </span>
    </li>
  );
}

export function ServiceBoardPanel() {
  const auth = useAuth();
  const gate = useServiceAnchorLayerGate();
  const canManageRates = auth?.status === "ready" && auth.hasRole(...SERVICE_ADMIN_ROLES);
  const [navTick, setNavTick] = useState(0);
  const m = useServiceBoardPanel(navTick);
  const notesSummary = useServiceResourceNotesSummary(navTick, m.rigs, m.techs);
  const [dismissBusy, setDismissBusy] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleWindow, setScheduleWindow] = useState("Morning");

  const handleRefresh = useCallback(() => {
    gate.refresh();
    setNavTick((s) => s + 1);
  }, [gate]);

  const handleDismissAlert = useCallback(
    async (noteId: string) => {
      setDismissBusy(true);
      await dismissServiceResourceNote(noteId);
      setDismissBusy(false);
      await notesSummary.reload();
    },
    [notesSummary]
  );

  const handleSchedule = useCallback(async () => {
    if (!m.selectedEvent || !scheduleDate) return;
    const ok = await m.scheduleEvent({
      event_id: m.selectedEvent.id,
      scheduled_date: scheduleDate,
      scheduled_window_label: scheduleWindow,
      tech_id: m.selectedEvent.tech_id,
      assigned_rig_id: m.selectedEvent.assigned_rig_id,
    });
    if (ok) setScheduleDate("");
  }, [m, scheduleDate, scheduleWindow]);

  const boardSections = groupServiceBoardEvents(m.queues, m.events);

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="min-w-0 space-y-1">
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Service board</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            Dispatch view — create a job, assign equipment, schedule. Configure offerings in Service
            setup (generic names; not a branded partner portal).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={inputClass}
            value={m.queueFilter}
            onChange={(e) => m.setQueueFilter(e.target.value)}
            disabled={!gate.enabled || m.listBusy}
          >
            <option value="all">All job types</option>
            {m.queues.map((q) => (
              <option key={q.id} value={q.id}>
                {q.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={ghostBtnClass}
            onClick={handleRefresh}
            disabled={gate.loading || m.listBusy}
          >
            {m.listBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <ServiceGateBanner gate={gate} />
      {gate.enabled && m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}

      {gate.enabled ? (
        <>
          <ServiceBoardResourceNotesSummary
            alerts={notesSummary.activeAlerts}
            onDismiss={(id) => void handleDismissAlert(id)}
            dismissBusy={dismissBusy}
          />

          <ServiceCollapsibleSection title="New work order" defaultOpen={false}>
            <ServiceRequestManualEntryPanel
              queues={m.queues}
              placeOnBoard
              onSubmitted={handleRefresh}
            />
          </ServiceCollapsibleSection>

          <div
            className={
              canManageRates
                ? "grid gap-4 lg:grid-cols-3 lg:items-start"
                : "space-y-4"
            }
          >
            <div className={canManageRates ? "space-y-4 lg:col-span-2" : "space-y-4"}>
              <ServiceCollapsibleSection title="Equipment dispatch" defaultOpen={false}>
                <ServiceDispatchBoard d={m} embedded />
              </ServiceCollapsibleSection>

              <ServiceCollapsibleSection title="Route composer" defaultOpen={false}>
                <ServiceRouteComposer d={m} embedded />
              </ServiceCollapsibleSection>
            </div>

            {canManageRates ? (
              <div className="lg:col-span-1 space-y-4">
                <ServiceCollapsibleSection title="Cost rates" defaultOpen={false}>
                  <ServiceLaborRatesSection navTick={navTick} onChanged={handleRefresh} />
                </ServiceCollapsibleSection>
              </div>
            ) : null}
          </div>

          {boardSections.length === 0 && !m.listBusy ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              No jobs on the board yet. Open New work order to add one.
            </p>
          ) : null}
          {boardSections.map((section) => (
            <SectionCard key={section.key} title={section.title}>
              <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                {section.events.map((ev) => (
                  <ServiceBoardEventListItem
                    key={ev.id}
                    ev={ev}
                    selected={m.selectedEventId === ev.id}
                    onToggle={() => m.setSelectedEventId((prev) => (prev === ev.id ? null : ev.id))}
                  />
                ))}
              </ul>
            </SectionCard>
          ))}

          {m.selectedEvent ? (
            <ServiceBoardEventDetail
              d={m}
              scheduleDate={scheduleDate}
              setScheduleDate={setScheduleDate}
              scheduleWindow={scheduleWindow}
              setScheduleWindow={setScheduleWindow}
              onSchedule={() => void handleSchedule()}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
